<script setup>
/**
 * KaptionChatListHeader
 *
 * Drop-in replacement for ChatListHeader when KAPTION_PLATFORM_CHAT_LIST
 * feature flag is enabled. Combines:
 * - Title area with context-aware actions (folder edit/delete, filter save/clear)
 * - KaptionPlatformActionTopbar (cycling filters, categories)
 * - Layout toggle
 *
 * NOTICE: KAPTION-INTEGRATION
 */
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { formatNumber } from '@chatwoot/utils';
import KaptionPlatformActionTopbar from './KaptionPlatformActionTopbar.vue';
// NOTICE: KAPTION-INTEGRATION - View switcher header (KAPTION_VIEW_SWITCHER)
import KaptionViewSwitcher from './KaptionViewSwitcher.vue';
import KaptionQuickFilters from './KaptionQuickFilters.vue';
import KaptionChatPreferencesButton from './KaptionChatPreferencesButton.vue';
// NOTICE: KAPTION-INTEGRATION - contact-first-conversation.BUTTON.1
import KaptionContactFirstButton from './KaptionContactFirst/KaptionContactFirstButton.vue';
import { useKaptionFeatureFlags } from 'dashboard/composables/useKaptionFeatureFlags';
import { getInboxIconByType } from 'dashboard/helper/inbox';
import SwitchLayout from 'dashboard/routes/dashboard/conversation/search/SwitchLayout.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { useUISettings } from 'dashboard/composables/useUISettings';
import wootConstants from 'dashboard/constants/globals';

const props = defineProps({
  // From ChatListHeader
  pageTitle: {
    type: String,
    default: '',
  },
  hasAppliedFilters: {
    type: Boolean,
    default: false,
  },
  hasActiveFolders: {
    type: Boolean,
    default: false,
  },
  // NOTICE: KAPTION-INTEGRATION - Full-path agent/team filter summary.
  hasRouteFilters: {
    type: Boolean,
    default: false,
  },
  isOnExpandedLayout: {
    type: Boolean,
    default: false,
  },
  conversationStats: {
    type: Object,
    default: () => ({}),
  },
  isListLoading: {
    type: Boolean,
    default: false,
  },

  // Kaption-specific
  isDarkMode: {
    type: Boolean,
    default: true,
  },
  favorites: {
    type: Array,
    default: () => [],
  },
  folders: {
    type: Array,
    default: () => [],
  },
  labels: {
    type: Array,
    default: () => [],
  },
  channels: {
    type: Array,
    default: () => [],
  },
  teams: {
    type: Array,
    default: () => [],
  },
  assistants: {
    type: Array,
    default: () => [],
  },
  currentAssigneeFilter: {
    type: String,
    default: 'all',
    validator: v => ['all', 'me', 'unassigned', 'unread'].includes(v),
  },
  // SID: mark-all-read.UI.1 — show the bulk Mark-all-read action in the header
  canMarkAllRead: {
    type: Boolean,
    default: false,
  },
  currentStatusFilter: {
    type: String,
    default: 'open',
    validator: v => ['open', 'resolved', 'snoozed', 'all'].includes(v),
  },

  // Selection state (for title display)
  selectedFavorite: {
    type: Object,
    default: null,
  },
  selectedFolder: {
    type: Object,
    default: null,
  },
  selectedLabel: {
    type: Object,
    default: null,
  },
  selectedChannel: {
    type: Object,
    default: null,
  },
  selectedTeam: {
    type: Object,
    default: null,
  },
  selectedAssistant: {
    type: Object,
    default: null,
  },
  // Whether we're on the "All Conversations" view (dashboard route with no category selected)
  isAllConversationsView: {
    type: Boolean,
    default: false,
  },
  // Current conversation type (mention, unattended, etc.)
  conversationType: {
    type: String,
    default: '',
  },
  // NOTICE: KAPTION-INTEGRATION - ChatList's assigneeTabItems (permission-
  // filtered, with counts) for the view switcher's quick-filter chips.
  assigneeTabs: {
    type: Array,
    default: () => [],
  },
  // NOTICE: KAPTION-INTEGRATION - People for the view switcher, and the agent
  // whose queue the list shows (agent_conversations route), if any.
  agents: {
    type: Array,
    default: () => [],
  },
  routeAgentId: {
    type: Number,
    default: null,
  },
  // Current sort order for conversations
  currentSort: {
    type: String,
    default: wootConstants.SORT_BY_TYPE.LAST_ACTIVITY_AT_DESC,
  },
  // NOTICE: KAPTION-INTEGRATION - ai-takeover.LIST.2 ("Quién atiende")
  showHandlerFilter: { type: Boolean, default: false },
  currentHandlerFilter: { type: String, default: '' },
  // NOTICE: KAPTION-INTEGRATION - Single-pane conversation search.
  showConversationSearch: {
    type: Boolean,
    default: false,
  },
  conversationSearchQuery: {
    type: String,
    default: '',
  },
});

const emit = defineEmits([
  // From ChatListHeader
  'addFolders',
  'deleteFolders',
  'resetFilters',
  'filtersModal',

  // Kaption-specific
  'filterChange',
  'statusFilterChange',
  'sortChange',
  // NOTICE: KAPTION-INTEGRATION - ai-takeover.LIST.2
  'handlerFilterChange',
  'favoriteClick',
  'folderClick',
  'labelClick',
  'channelClick',
  'teamClick',
  'assistantClick',
  // NOTICE: KAPTION-INTEGRATION
  'agentClick',
  'createFilter',
  'toggleFavorite',
  'navigateToMentions',
  'navigateToUnattended',
  'navigateToAllConversations',
  'markAllRead',
  'conversationSearchQueryChange',
]);

const { t } = useI18n();

// NOTICE: KAPTION-INTEGRATION - The view switcher replaces the cycling pill,
// category icons and chevron menu of KaptionPlatformActionTopbar.
const { isKaptionFlagEnabled } = useKaptionFeatureFlags();
const useViewSwitcher = computed(() =>
  isKaptionFlagEnabled('KAPTION_VIEW_SWITCHER')
);
// Creating custom filters from the switcher ships on its own flag.
const canCreateFilter = computed(
  () => useViewSwitcher.value && isKaptionFlagEnabled('KAPTION_SAVED_FILTERS')
);

// Layout toggle (handled internally like original ChatListHeader)
const { uiSettings, updateUISettings } = useUISettings();

// Computed: display title based on selection and context
const displayTitle = computed(() => {
  if (props.hasRouteFilters) return props.pageTitle;
  // Selection-based titles take priority
  if (props.selectedFavorite) return props.selectedFavorite.name;
  if (props.selectedFolder) return props.selectedFolder.name;
  if (props.selectedLabel) return `#${props.selectedLabel.name}`;
  if (props.selectedChannel) return props.selectedChannel.name;
  if (props.selectedTeam) return props.selectedTeam.name;
  if (props.selectedAssistant) return props.selectedAssistant.name;

  // Special conversation type titles (mentions, unattended)
  if (props.conversationType === 'mention')
    return t('KAPTION_PLATFORM.CHAT_LIST.MENTIONS');
  if (props.conversationType === 'unattended')
    return t('KAPTION_PLATFORM.CHAT_LIST.UNATTENDED');

  // Context-based titles
  if (props.hasActiveFolders) return props.pageTitle; // folder name from ChatList
  if (props.hasAppliedFilters)
    return t('KAPTION_PLATFORM.CHAT_LIST.FILTERED_RESULTS');

  // Default based on assignee filter
  const titleKeys = {
    all: 'KAPTION_PLATFORM.CHAT_LIST.ALL_CONVERSATIONS',
    me: 'KAPTION_PLATFORM.CHAT_LIST.MY_CONVERSATIONS',
    unassigned: 'KAPTION_PLATFORM.CHAT_LIST.UNASSIGNED',
    // NOTICE: KAPTION-INTEGRATION - Unread filter title
    unread: 'KAPTION_PLATFORM.CHAT_LIST.UNREAD',
  };
  return t(
    titleKeys[props.currentAssigneeFilter] ||
      'KAPTION_PLATFORM.CHAT_LIST.CONVERSATIONS'
  );
});

// NOTICE: KAPTION-INTEGRATION
// Computed: display subtitle for selected channel (inbox)
// Shows phone number for WhatsApp/Twilio, instagram handle for Instagram, etc.
const displaySubtitle = computed(() => {
  if (!props.selectedChannel) return null;

  const channel = props.selectedChannel;
  const channelType = channel.channel_type;

  // WhatsApp: show phone number
  if (channelType === 'Channel::Whatsapp' && channel.phone_number) {
    return channel.phone_number;
  }

  // Instagram: show instagram_id prefixed with @
  if (channelType === 'Channel::Instagram' && channel.instagram_id) {
    return `@${channel.instagram_id}`;
  }

  // Twilio SMS: show phone number
  if (channelType === 'Channel::TwilioSms' && channel.phone_number) {
    return channel.phone_number;
  }

  // Email: show email address
  if (channelType === 'Channel::Email' && channel.email) {
    return channel.email;
  }

  // Telegram: show bot name prefixed with @
  if (channelType === 'Channel::Telegram' && channel.bot_name) {
    return `@${channel.bot_name}`;
  }

  return null;
});

// NOTICE: KAPTION-INTEGRATION - View switcher title, caption and selection
const FAVORITE_KIND = {
  label: { key: 'LABEL', icon: 'i-lucide-tag' },
  inbox: { key: 'INBOX', icon: 'i-lucide-inbox' },
  team: { key: 'TEAM', icon: 'i-lucide-users' },
  folder: { key: 'FOLDER', icon: 'i-lucide-folder' },
  assistant: { key: 'ASSISTANT', icon: 'i-lucide-bot' },
};

const VIEW_ICONS = {
  all: 'i-lucide-inbox',
  me: 'i-lucide-user-check',
  unassigned: 'i-lucide-circle-dashed',
  unread: 'i-lucide-mail',
};

const inboxIcon = inbox =>
  getInboxIconByType(inbox?.channel_type, inbox?.medium, 'line');

// What the list is showing: caption ("Label", "Person"...), leading icon or
// label color, and the switcher entry to highlight.
const switcherContext = computed(() => {
  const favorite = props.selectedFavorite;
  if (props.routeAgentId) {
    return {
      kindKey: 'PERSON',
      icon: 'i-lucide-user-round',
      activeKey: `agent:${props.routeAgentId}`,
    };
  }
  if (props.hasRouteFilters) return { icon: 'i-lucide-users' };
  if (favorite) {
    const kind = FAVORITE_KIND[favorite.type] || {};
    return {
      kindKey: kind.key,
      icon:
        favorite.type === 'inbox' ? inboxIcon(favorite.resolved) : kind.icon,
      color: favorite.type === 'label' ? favorite.resolved?.color : '',
      activeKey: `${favorite.type}:${favorite.id}`,
    };
  }
  if (props.selectedFolder) {
    return {
      kindKey: 'FOLDER',
      icon: 'i-lucide-folder',
      activeKey: `folder:${props.selectedFolder.id}`,
    };
  }
  if (props.selectedLabel) {
    return {
      kindKey: 'LABEL',
      icon: 'i-lucide-tag',
      color: props.selectedLabel.color,
      activeKey: `label:${props.selectedLabel.id}`,
    };
  }
  if (props.selectedChannel) {
    return {
      kindKey: 'INBOX',
      icon: inboxIcon(props.selectedChannel),
      activeKey: `inbox:${props.selectedChannel.id}`,
    };
  }
  if (props.selectedTeam) {
    return {
      kindKey: 'TEAM',
      icon: 'i-lucide-users',
      activeKey: `team:${props.selectedTeam.id}`,
    };
  }
  if (props.selectedAssistant) {
    return {
      kindKey: 'ASSISTANT',
      icon: 'i-lucide-bot',
      activeKey: `assistant:${props.selectedAssistant.id}`,
    };
  }
  if (props.conversationType === 'mention') {
    return { icon: 'i-lucide-at-sign', activeKey: 'view:mentions' };
  }
  if (props.conversationType === 'unattended') {
    return {
      icon: 'i-lucide-message-circle-warning',
      activeKey: 'view:unattended',
    };
  }
  if (props.hasActiveFolders) return { icon: 'i-lucide-folder' };
  if (props.hasAppliedFilters) return { icon: 'i-lucide-filter' };
  return {
    icon: VIEW_ICONS[props.currentAssigneeFilter] || VIEW_ICONS.all,
    activeKey: props.isAllConversationsView ? 'view:all' : '',
  };
});

const switcherKind = computed(() =>
  switcherContext.value.kindKey
    ? t(`KAPTION_PLATFORM.VIEW_SWITCHER.KIND.${switcherContext.value.kindKey}`)
    : ''
);

const switcherTitle = computed(() => {
  // The caption already says "Label", so drop the "#" prefix there.
  if (!props.hasRouteFilters && props.selectedLabel && !props.selectedFavorite)
    return props.selectedLabel.name;
  // The unfiltered list is just "Conversations", not "All Conversations".
  if (switcherContext.value.activeKey === 'view:all')
    return t('KAPTION_PLATFORM.VIEW_SWITCHER.ALL_CONVERSATIONS');
  return displayTitle.value;
});

// NOTICE: KAPTION-INTEGRATION - The advanced filter (750px) and the save-filter
// popup are wider than the chat list. Warm Paper clips the list card
// (overflow: hidden), so an absolutely positioned anchor inside it cuts them
// off. Pin each anchor to the viewport under its trigger instead, clamped to
// the window, so the popup opens across the conversation pane.
const FILTER_PANEL_WIDTH = 750;
const SAVE_PANEL_WIDTH = 520;
const VIEWPORT_MARGIN = 16;
const filterAnchorEl = ref(null);
const saveAnchorEl = ref(null);
const filterTargetStyle = ref(null);
const saveTargetStyle = ref(null);

function pinnedStyle(el, width) {
  if (!el) return null;
  const rect = el.getBoundingClientRect();
  const maxLeft = Math.max(
    VIEWPORT_MARGIN,
    window.innerWidth - width - VIEWPORT_MARGIN
  );
  return {
    position: 'fixed',
    top: `${rect.bottom + 8}px`,
    left: `${Math.min(Math.max(rect.left, VIEWPORT_MARGIN), maxLeft)}px`,
    marginTop: '0',
  };
}

function repinAnchors() {
  if (filterTargetStyle.value)
    filterTargetStyle.value = pinnedStyle(
      filterAnchorEl.value,
      FILTER_PANEL_WIDTH
    );
  if (saveTargetStyle.value)
    saveTargetStyle.value = pinnedStyle(saveAnchorEl.value, SAVE_PANEL_WIDTH);
}

function openFiltersModal() {
  if (useViewSwitcher.value)
    filterTargetStyle.value = pinnedStyle(
      filterAnchorEl.value,
      FILTER_PANEL_WIDTH
    );
  emit('filtersModal');
}

// "+ Nuevo filtro" from the Filters panel: same pinned anchor as the header
// button used to get, so Warm Paper doesn't clip the advanced filter.
function openCreateFilter() {
  filterTargetStyle.value = pinnedStyle(
    filterAnchorEl.value,
    FILTER_PANEL_WIDTH
  );
  emit('createFilter');
}

const viewSwitcherRef = ref(null);
function openFiltersPanel() {
  // After the click finishes: the panel closes on any outside click, and the
  // Filtros button is outside it, so opening synchronously would be undone
  // by that same click.
  setTimeout(() => viewSwitcherRef.value?.open(), 0);
}

function openSaveFilter() {
  saveTargetStyle.value = pinnedStyle(saveAnchorEl.value, SAVE_PANEL_WIDTH);
  emit('addFolders');
}

onMounted(() => window.addEventListener('resize', repinAnchors));
onBeforeUnmount(() => window.removeEventListener('resize', repinAnchors));

// Same rule the topbar's cycling pill used: on the top-level views the
// assignee chips navigate home; inside a category they filter in place.
const quickFiltersNavigate = computed(
  () =>
    props.isAllConversationsView ||
    props.conversationType === 'mention' ||
    props.conversationType === 'unattended'
);

// Computed: conversation count
const allCount = computed(() => props.conversationStats?.allCount || 0);
const formattedAllCount = computed(() => formatNumber(allCount.value));

// Computed: show count badge
const showCountBadge = computed(() => {
  // NOTICE: KAPTION-INTEGRATION - the applied-filter strip carries the count.
  if (
    useViewSwitcher.value &&
    props.hasAppliedFilters &&
    !props.hasActiveFolders
  )
    return false;
  return (
    allCount.value > 0 &&
    (props.hasAppliedFilters || props.hasActiveFolders) &&
    !props.isListLoading
  );
});

// Computed: disable cycling filter when folders/filters are active
const disableCyclingFilter = computed(() => {
  return props.hasActiveFolders || props.hasAppliedFilters;
});

// Map assignee filter values for topbar
// ChatList uses 'me', topbar uses 'mine'
// NOTICE: KAPTION-INTEGRATION - 'unread' passes through unchanged.
const topbarAssigneeFilter = computed(() => {
  // When on mentions/unattended route, show that as the current filter
  if (props.conversationType === 'mention') return 'mentions';
  if (props.conversationType === 'unattended') return 'unattended';
  // Map assignee filter values for topbar - ChatList uses 'me', topbar uses 'mine'
  const mapping = {
    all: 'all',
    me: 'mine',
    unassigned: 'unassigned',
    unread: 'unread',
  };
  return mapping[props.currentAssigneeFilter] || 'all';
});

// Event handlers - bubble up to parent
function handleFilterChange(filter) {
  emit('filterChange', filter);
}

// NOTICE: KAPTION-INTEGRATION - Quick filters keep the current view selected.
function handleQuickFilterChange(filter) {
  emit('filterChange', filter, { keepSelection: true });
}

function handleStatusFilterChange(status) {
  emit('statusFilterChange', status);
}

function handleSortChange(sortValue) {
  emit('sortChange', sortValue);
}

function handleFavoriteClick(favorite) {
  emit('favoriteClick', favorite);
}

function handleFolderClick(folder) {
  emit('folderClick', folder);
}

function handleLabelClick(label) {
  emit('labelClick', label);
}

function handleChannelClick(channel) {
  emit('channelClick', channel);
}

function handleTeamClick(team) {
  emit('teamClick', team);
}

function handleAssistantClick(assistant) {
  emit('assistantClick', assistant);
}

function handleToggleFavorite(payload) {
  emit('toggleFavorite', payload);
}

// Navigation handlers for extended filter cycle
function handleNavigateToMentions() {
  emit('navigateToMentions');
}

function handleNavigateToUnattended() {
  emit('navigateToUnattended');
}

function handleNavigateToAllConversations(filter) {
  emit('navigateToAllConversations', filter);
}

// "Quitar" in the filter menu: drop whatever the Filters panel applied.
function clearActiveFilter() {
  if (props.hasAppliedFilters && !props.hasActiveFolders) {
    emit('resetFilters');
    return;
  }
  handleNavigateToAllConversations('all');
}

// Layout toggle - handled internally like original ChatListHeader
function handleToggleLayout() {
  const { LAYOUT_TYPES } = wootConstants;
  const {
    conversation_display_type: conversationDisplayType = LAYOUT_TYPES.CONDENSED,
  } = uiSettings.value;
  const newViewType =
    conversationDisplayType === LAYOUT_TYPES.CONDENSED
      ? LAYOUT_TYPES.EXPANDED
      : LAYOUT_TYPES.CONDENSED;
  updateUISettings({
    conversation_display_type: newViewType,
    previously_used_conversation_display_type: newViewType,
  });
}

function handleConversationSearchInput(event) {
  emit('conversationSearchQueryChange', event.target.value);
}

function clearConversationSearch() {
  emit('conversationSearchQueryChange', '');
}

// Text/background color classes based on dark mode
// Use transparent background to inherit from parent (ChatList uses bg-n-solid-1)
const containerClass = computed(() => 'bg-transparent');

const titleTextClass = computed(() =>
  props.isDarkMode ? 'text-white' : 'text-gray-900'
);

// NOTICE: KAPTION-INTEGRATION - Subtitle styling for inbox identifiers
const subtitleTextClass = computed(() =>
  props.isDarkMode ? 'text-white/60' : 'text-gray-500'
);

const countBadgeClass = computed(() =>
  props.isDarkMode ? 'bg-white/10 text-white/70' : 'bg-gray-100 text-gray-600'
);

const conversationSearchPlaceholder = computed(() =>
  t('KAPTION_PLATFORM.CONVERSATION_SEARCH.PLACEHOLDER')
);
const clearConversationSearchLabel = computed(() =>
  t('KAPTION_PLATFORM.CONVERSATION_SEARCH.CLEAR')
);
</script>

<template>
  <div :class="containerClass">
    <!-- Title Area -->
    <div
      class="flex items-center justify-between gap-2 px-3 py-2"
      :class="{
        'border-b border-n-weak': hasAppliedFilters || hasActiveFolders,
      }"
    >
      <!-- Title + Subtitle + Count -->
      <div
        class="flex items-center min-w-0 gap-2"
        :class="useViewSwitcher ? 'flex-1' : 'justify-center'"
      >
        <!-- NOTICE: KAPTION-INTEGRATION - Title doubles as the view switcher -->
        <KaptionViewSwitcher
          v-if="useViewSwitcher"
          ref="viewSwitcherRef"
          :show-views="false"
          :title="switcherTitle"
          :kind="switcherKind"
          :subtitle="displaySubtitle || ''"
          :favorites="favorites"
          :folders="folders"
          :labels="labels"
          :channels="channels"
          :teams="teams"
          :assistants="assistants"
          :icon="switcherContext.icon"
          :color="switcherContext.color || ''"
          :agents="agents"
          :active-key="switcherContext.activeKey || ''"
          :all-count="conversationStats?.allCount || 0"
          :can-create-filter="canCreateFilter"
          @favorite-click="handleFavoriteClick"
          @folder-click="handleFolderClick"
          @label-click="handleLabelClick"
          @channel-click="handleChannelClick"
          @team-click="handleTeamClick"
          @assistant-click="handleAssistantClick"
          @agent-click="agent => emit('agentClick', agent)"
          @create-filter="openCreateFilter"
          @toggle-favorite="handleToggleFavorite"
          @navigate-to-mentions="handleNavigateToMentions"
          @navigate-to-unattended="handleNavigateToUnattended"
          @navigate-to-all-conversations="handleNavigateToAllConversations"
        />
        <div v-else class="flex flex-col min-w-0">
          <h1
            class="text-sm font-semibold truncate"
            :class="titleTextClass"
            :title="displayTitle"
          >
            {{ displayTitle }}
          </h1>
          <!-- NOTICE: KAPTION-INTEGRATION - Subtitle showing inbox identifier -->
          <span
            v-if="displaySubtitle"
            class="text-xs truncate leading-tight"
            :class="subtitleTextClass"
            :title="displaySubtitle"
          >
            {{ displaySubtitle }}
          </span>
        </div>
        <!-- NOTICE: KAPTION-INTEGRATION - rounded-full for pill-shaped appearance -->
        <span
          v-if="showCountBadge"
          class="px-2 py-0.5 text-xs shrink-0 rounded-full"
          :class="countBadgeClass"
          :title="String(allCount)"
        >
          {{ formattedAllCount }}
        </span>
      </div>

      <!-- Action Buttons (right side) -->
      <div class="flex items-center gap-1">
        <!-- NOTICE: KAPTION-INTEGRATION - contact-first-conversation.BUTTON.1
             "Nuevo mensaje" (self-gated on its flags and WhatsApp inboxes) -->
        <KaptionContactFirstButton />
        <!-- Folder actions when folder is active -->
        <template v-if="hasActiveFolders">
          <div class="relative">
            <NextButton
              id="kaptionEditFolderButton"
              v-tooltip.top-end="$t('FILTER.CUSTOM_VIEWS.EDIT.EDIT_BUTTON')"
              icon="i-lucide-pen-line"
              slate
              xs
              faded
              @click="openFiltersModal"
            />
            <!-- Teleport target for ConversationFilter modal -->
            <div
              v-if="!useViewSwitcher"
              id="conversationFilterTeleportTarget"
              class="absolute z-50 mt-2"
              :class="{ 'ltr:right-0 rtl:left-0': isOnExpandedLayout }"
            />
            <div
              id="kaptionFolderEditTeleportTarget"
              class="absolute z-50 mt-2"
              :class="{ 'ltr:right-0 rtl:left-0': isOnExpandedLayout }"
            />
          </div>
          <NextButton
            id="kaptionDeleteFolderButton"
            v-tooltip.top-end="$t('FILTER.CUSTOM_VIEWS.DELETE.DELETE_BUTTON')"
            icon="i-lucide-trash-2"
            ruby
            xs
            faded
            @click="emit('deleteFolders')"
          />
        </template>

        <!-- Filter actions when filters are applied (but not a folder).
             NOTICE: KAPTION-INTEGRATION - the view switcher shows them in a
             strip under the title instead (see below). -->
        <template v-else-if="hasAppliedFilters && !useViewSwitcher">
          <div class="relative">
            <NextButton
              v-tooltip.top-end="$t('FILTER.CUSTOM_VIEWS.ADD.SAVE_BUTTON')"
              icon="i-lucide-save"
              slate
              xs
              faded
              @click="emit('addFolders')"
            />
            <div
              id="kaptionSaveFilterTeleportTarget"
              class="absolute z-50 mt-2"
              :class="{ 'ltr:right-0 rtl:left-0': isOnExpandedLayout }"
            />
          </div>
          <NextButton
            v-tooltip.top-end="$t('FILTER.CLEAR_BUTTON_LABEL')"
            icon="i-lucide-circle-x"
            ruby
            faded
            xs
            @click="emit('resetFilters')"
          />
        </template>

        <!-- NOTICE: KAPTION-INTEGRATION - Bulk Mark-all-read (Unread tab only) -->
        <!-- SID: mark-all-read.UI.1 -->
        <NextButton
          v-if="canMarkAllRead"
          v-tooltip.top-end="$t('CHAT_LIST.MARK_ALL_READ.BUTTON')"
          icon="i-lucide-check-check"
          slate
          xs
          faded
          @click="emit('markAllRead')"
        />

        <!-- NOTICE: KAPTION-INTEGRATION - Custom filter button + the one anchor
             the advanced filter modal teleports into (a folder's edit button
             above reuses it). -->
        <div v-if="useViewSwitcher" ref="filterAnchorEl" class="relative">
          <!-- Opens across the conversation pane, like Chatwoot's own header;
               right-aligned only when the list takes the whole width. -->
          <div
            id="conversationFilterTeleportTarget"
            class="absolute z-50 mt-2"
            :class="{ 'ltr:right-0 rtl:left-0': isOnExpandedLayout }"
            :style="filterTargetStyle"
          />
        </div>

        <!-- NOTICE: KAPTION-INTEGRATION - Chat preferences, moved out of the filter menu -->
        <KaptionChatPreferencesButton v-if="useViewSwitcher" />

        <!-- Layout toggle (always visible) -->
        <SwitchLayout
          :is-on-expanded-layout="isOnExpandedLayout"
          @toggle="handleToggleLayout"
        />
      </div>
    </div>

    <!-- NOTICE: KAPTION-INTEGRATION - Unsaved custom filter: results + Save
         + Clear on their own line, so the title keeps its room. -->
    <div
      v-if="useViewSwitcher && hasAppliedFilters && !hasActiveFolders"
      data-testid="kaption-applied-filter-bar"
      class="flex items-center gap-2 px-3 pb-2"
    >
      <span class="flex-1 min-w-0 text-xs truncate text-n-slate-11">
        {{
          t('KAPTION_PLATFORM.VIEW_SWITCHER.FILTER_RESULTS', {
            count: formattedAllCount,
          })
        }}
      </span>
      <div ref="saveAnchorEl" class="relative">
        <NextButton
          data-testid="kaption-save-filter"
          :label="$t('FILTER.CUSTOM_VIEWS.ADD.SAVE_BUTTON')"
          icon="i-lucide-save"
          teal
          xs
          faded
          @click="openSaveFilter"
        />
        <div
          id="kaptionSaveFilterTeleportTarget"
          class="absolute z-50 mt-2"
          :style="saveTargetStyle"
        />
      </div>
      <NextButton
        data-testid="kaption-clear-filter"
        :label="$t('FILTER.CLEAR_BUTTON_LABEL')"
        slate
        xs
        ghost
        @click="emit('resetFilters')"
      />
    </div>

    <!-- NOTICE: KAPTION-INTEGRATION - WhatsApp Web-style search stays in the chat-list pane. -->
    <div v-if="showConversationSearch" class="px-3 pb-2">
      <div
        class="flex items-center w-full gap-2 px-2 py-1 text-sm leading-5 rounded-2xl h-7 outline outline-1 outline-n-weak bg-n-solid-3 dark:bg-n-black/30 text-n-slate-12 focus-within:outline-n-brand/50 focus-within:bg-n-solid-2"
      >
        <span class="i-lucide-search size-4 text-n-slate-9 shrink-0" />
        <input
          :value="conversationSearchQuery"
          type="text"
          role="searchbox"
          class="kaption-conversation-search-input w-full min-w-0 h-5 text-sm bg-transparent text-n-slate-12"
          :placeholder="conversationSearchPlaceholder"
          autocomplete="off"
          @input="handleConversationSearchInput"
        />
        <button
          v-if="conversationSearchQuery"
          type="button"
          class="text-n-slate-9 hover:text-n-slate-12"
          :aria-label="clearConversationSearchLabel"
          @click="clearConversationSearch"
        >
          <span class="i-lucide-x size-4" />
        </button>
      </div>
    </div>

    <!-- NOTICE: KAPTION-INTEGRATION - Quick filters under the view switcher -->
    <KaptionQuickFilters
      v-if="useViewSwitcher"
      :assignee-tabs="assigneeTabs"
      :current-assignee-filter="currentAssigneeFilter"
      :current-status-filter="currentStatusFilter"
      :current-sort="currentSort"
      :show-handler-filter="showHandlerFilter"
      :current-handler-filter="currentHandlerFilter"
      :navigates="quickFiltersNavigate"
      :disabled="disableCyclingFilter"
      :conversation-type="conversationType"
      :filters-active="
        Boolean(switcherContext.kindKey) ||
        hasActiveFolders ||
        hasAppliedFilters
      "
      :active-filter-name="switcherTitle"
      @filter-change="handleQuickFilterChange"
      @navigate-to-unattended="handleNavigateToUnattended"
      @navigate-to-mentions="handleNavigateToMentions"
      @open-filters="openFiltersPanel"
      @clear-filters="clearActiveFilter"
      @navigate-to-all-conversations="handleNavigateToAllConversations"
      @status-filter-change="handleStatusFilterChange"
      @sort-change="handleSortChange"
      @handler-filter-change="handler => emit('handlerFilterChange', handler)"
    />

    <!-- Kaption Platform Action Topbar -->
    <KaptionPlatformActionTopbar
      v-else
      :is-dark-mode="isDarkMode"
      :favorites="favorites"
      :folders="folders"
      :labels="labels"
      :channels="channels"
      :teams="teams"
      :assistants="assistants"
      :initial-filter="topbarAssigneeFilter"
      :initial-status-filter="currentStatusFilter"
      :current-sort="currentSort"
      :disable-cycling-filter="disableCyclingFilter"
      show-settings-panel
      :selected-team="selectedTeam"
      :selected-channel="selectedChannel"
      :selected-label="selectedLabel"
      :selected-folder="selectedFolder"
      :selected-assistant="selectedAssistant"
      :is-all-conversations-view="isAllConversationsView"
      :conversation-type="conversationType"
      @filter-change="handleFilterChange"
      @status-filter-change="handleStatusFilterChange"
      @sort-change="handleSortChange"
      @favorite-click="handleFavoriteClick"
      @folder-click="handleFolderClick"
      @label-click="handleLabelClick"
      @channel-click="handleChannelClick"
      @team-click="handleTeamClick"
      @assistant-click="handleAssistantClick"
      @toggle-favorite="handleToggleFavorite"
      @navigate-to-mentions="handleNavigateToMentions"
      @navigate-to-unattended="handleNavigateToUnattended"
      @navigate-to-all-conversations="handleNavigateToAllConversations"
    />
  </div>
</template>

<style scoped>
.kaption-conversation-search-input {
  height: 20px !important;
  min-height: 20px !important;
  margin: 0 !important;
  padding: 0 !important;
  border: 0 !important;
  border-radius: 0 !important;
  outline: 0 !important;
  box-shadow: none !important;
  background: transparent !important;
  font-size: 14px !important;
  line-height: 20px !important;
  appearance: none !important;
  -webkit-appearance: none !important;
}

.kaption-conversation-search-input:focus,
.kaption-conversation-search-input:focus-visible {
  border: 0 !important;
  outline: 0 !important;
  box-shadow: none !important;
}

.kaption-conversation-search-input::placeholder {
  color: currentColor !important;
  opacity: 0.45 !important;
}
</style>
